import { Link } from "react-router-dom";
import { Zap, Moon, CalendarClock, CalendarRange, Sun, Hourglass, ArrowRight } from "lucide-react";
import { useLang, useSiteSettings } from "@/context/LanguageContext";
import { useOffers, offerKindName, offerGenericHint } from "@/lib/offers";
import { translations } from "@/lib/translations";

export const OFFER_ICONS = { last_minute: Zap, free_nights: Moon, early_bird: CalendarClock, long_stay: Hourglass, gap: CalendarRange, season: Sun };

/** Streifen mit allen aktiven Angebots-Kategorien (Startseite / Angebote-Seite) – allgemein gehalten (jeder Vermieter hat eigene Zahlen), Klick führt zur gefilterten Objektliste. */
export const OffersStrip = ({ compact = false, activeId = null, force = false }) => {
  const { lang, t } = useLang();
  const site = useSiteSettings();
  const offers = useOffers();
  if (!offers.length || (!force && site.offers_show_home === false)) return null;
  return (
    <section className={`max-w-7xl mx-auto px-6 lg:px-10 ${compact ? "pb-8" : "home-section"}`} data-testid="offers-strip">
      {!compact && (
        <div className="home-section-head">
          <div className="text-xs uppercase tracking-[0.3em] text-signal flex items-center gap-2"><Zap className="w-4 h-4" />{t.offers.subtitle}</div>
          <h2 className="font-serif text-4xl md:text-5xl text-stone-900 mt-3">{t.offers.title}</h2>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {offers.map((o, i) => {
          const Icon = OFFER_ICONS[o.kind] || Zap;
          const active = activeId === o.id;
          const scope = [o.island && translations[lang]?.islands?.[o.island], o.property_type && t.types?.[o.property_type]].filter(Boolean).join(" · ");
          return (
            <Link key={o.id} to={active ? "/properties?deals=1" : `/properties?offer=${o.id}`} style={{ animationDelay: `${i * 60}ms` }}
                  className={`group relative overflow-hidden rounded-2xl border p-5 flex gap-4 items-start transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md ${active ? "border-signal bg-red-50" : "border-stone-200 bg-white hover:border-signal/60"}`}
                  data-testid={`offer-card-${o.id}`}>
              <div className="shrink-0 w-11 h-11 rounded-full bg-signal/10 text-signal flex items-center justify-center"><Icon className="w-5 h-5" /></div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-stone-900 leading-tight" data-testid="offer-card-title">{offerKindName(o, t, lang)}</div>
                <div className="text-xs text-stone-500 mt-1">{offerGenericHint(o, t)}{scope ? ` · ${scope}` : ""}</div>
                <div className="text-xs font-semibold text-signal mt-2 inline-flex items-center gap-1">{active ? t.offers.showAll : t.offers.view} <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" /></div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};
